import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./firebaseConfig";
import LandingPage from './components/LandingPage';
import HomePage from './components/HomePage';

// Pages
import Login from "./Login.js";
import Dashboard from "./Dashboard";
import Signup from "./Signup";
import ManageUsers from "./ManageUsers";
import CreateQuestions from "./CreateQuestions";
import EditQuestion from "./EditQuestion";
import CreateQuiz from "./CreateQuiz";
import ViewQuizzes from "./ViewQuizzes";
import QuizSuccess from "./QuizSuccess";
import EnterQuiz from "./EnterQuiz";
import ViewQuizResponses from "./ViewQuizResponses"; // For admin/teacher
import ViewMyScores from "./ViewMyScores";          // NEW COMPONENT for students
import TypingTest from "./TypingTest";              // NEW COMPONENT for speed typing test
import QuizResults from "./QuizResults";            // NEW COMPONENT for viewing quiz results
import JoinTeacher from "./joinTeacher";            // NEW COMPONENT for students to join a teacher
import ViewStudents from "./ViewStudents";          // NEW COMPONENT for teachers to view their students
import StudentProfile from "./StudentProfile";      // NEW COMPONENT for viewing detailed student analytics
import AutoReport from "./AutoReport";              // NEW COMPONENT for generating student reports


const App = () => {
  const [user, setUser] = useState(null); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null);

  useEffect(() => {
    const auth = getAuth();

    // Monitor authentication state
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Check if user exists in Firestore
          const userDocRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userDocRef);

          if (!userSnap.exists()) {
            // Create a new user document if it doesn't exist
            await setDoc(userDocRef, {
              role: "student", // Default role for new users
              email: currentUser.email,
              username: currentUser.displayName || "New User",
              displayName: currentUser.displayName || "New User",
              createdAt: new Date(),
            });
          }

          // Update user state with role and Firestore data
          const userData = userSnap.exists() ? userSnap.data() : { role: "student" };
          setUser({ ...currentUser, role: userData.role });
          console.log("CURRENT USER PROPERTIES", { ...currentUser, role: userData.role });
        } catch (error) {
          console.error("Error fetching or creating user profile:", error);
          setError("An error occurred while fetching your profile.");
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <h2>Loading, please wait...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "Arial, sans-serif",
          color: "red",
        }}
      >
        <h2>{error}</h2>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/dashboard"
          element={user ? <Dashboard user={user} /> : <Navigate to="/" />}
        />
        <Route
          path="/manage-users"
          element={
            user && user.role === "admin" ? (
              <ManageUsers user={user} />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/create-questions"
          element={
            user && (user.role === "admin" || user.role === "teacher") ? (
              <CreateQuestions user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/edit-question/:questionId"
          element={
            user && (user.role === "admin" || user.role === "teacher") ? (
              <EditQuestion user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/create-quiz"
          element={
            user && (user.role === "admin" || user.role === "teacher") ? (
              <CreateQuiz user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/view-quiz-responses"
          element={
            user && (user.role === "admin" || user.role === "teacher") ? (
              <ViewQuizResponses user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/view-quizzes"
          element={
            user && (user.role === "admin" || user.role === "teacher") ? (
              <ViewQuizzes user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/quiz-results/:quizCode"
          element={
            user && (user.role === "admin" || user.role === "teacher") ? (
              <QuizResults user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/quiz-success"
          element={
            user && (user.role === "admin" || user.role === "teacher") ? (
              <QuizSuccess user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/enter-quiz"
          element={
            user && user.role === "student" ? (
              <EnterQuiz user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/view-my-scores"
          element={
            user && user.role === "student" ? (
              <ViewMyScores user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/typing-test"
          element={
            user && (user.role === "student" || user.role === "teacher") ? (
              <TypingTest user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/join-teacher"
          element={
            user && user.role === "student" ? (
              <JoinTeacher user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/view-students"
          element={
            user && user.role === "teacher" ? (
              <ViewStudents user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/student-profile/:studentId"
          element={
            user && (user.role === "teacher" || user.role === "admin") ? (
              <StudentProfile user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/auto-report"
          element={
            user && (user.role === "admin" || user.role === "teacher") ? (
              <AutoReport user={user} />
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
